import { Button } from "@/components/ui/button.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { PlusIcon, MessageSquareIcon, Loader2 } from "lucide-react";
import { useAvaChats, useCreateAvaChat } from "@/lib/api/ava.ts";
import { useNavigate, useParams } from "@tanstack/react-router";

export function AvaChatSidebar() {
  const { data: chats = [], isLoading } = useAvaChats();
  const createChatMutation = useCreateAvaChat();
  const navigate = useNavigate();
  const params = useParams({ from: "/_authenticated/ava" });

  // Get selected chat ID from route params (will be undefined on index route)
  const selectedChatId = "chatId" in params ? params.chatId : null;

  const handleNewChat = async () => {
    try {
      const newChat = await createChatMutation.mutateAsync("New Chat");
      navigate({ to: "/ava/$chatId", params: { chatId: newChat.id } });
    } catch (error) {
      console.error("Failed to create new chat:", error);
    }
  };

  const handleSelectChat = (chatId: string) => {
    navigate({ to: "/ava/$chatId", params: { chatId } });
  };

  return (
    <div className="flex h-full w-80 flex-col border-r border-gray-200 bg-white">
      <div className="border-b border-gray-200 p-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">AVA</h1>
          <Button
            onClick={handleNewChat}
            disabled={createChatMutation.isPending}
            size="sm"
            className="flex items-center gap-2"
          >
            {createChatMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlusIcon className="h-4 w-4" />
            )}
            New Chat
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          Your AI assistant for teen mentorship insights
        </p>
      </div>

      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="p-2">
            {chats.length === 0 ? (
              <div className="py-8 text-center">
                <MessageSquareIcon className="mx-auto mb-3 h-12 w-12 text-gray-400" />
                <p className="text-sm text-gray-500">No chats yet</p>
                <p className="mt-1 text-xs text-gray-400">
                  Create your first chat to get started
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {chats.map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => handleSelectChat(chat.id)}
                    className={`w-full rounded-lg p-3 text-left transition-colors ${
                      selectedChatId === chat.id
                        ? "border border-blue-200 bg-blue-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="truncate text-sm font-medium text-gray-900">
                      {chat.title}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {new Date(chat.updatedAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      <Separator />

      <div className="p-4">
        <div className="text-center text-xs text-gray-500">
          AVA helps you understand teens better through AI-powered insights
        </div>
      </div>
    </div>
  );
}
